import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Leaf, Flame, AlertTriangle, Info, Lock, Sparkles } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { PRO_INFLAMMATORY, ANTI_INFLAMMATORY, E_NUMBERS, FOOD_DISCLAIMER } from "../foodguide";
import { hasFullAccess } from "../data";
import { useAsaAuth } from "../AuthContext";

const concernStyle = {
  Skin: "bg-[#fdeceb] text-[#e0567a]",
  "Behaviour/Skin": "bg-[#fdf2e8] text-[#e08a2b]",
  Behaviour: "bg-[#fdf2e8] text-[#e08a2b]",
  "Skin/Asthma": "bg-[#fdeceb] text-[#e0567a]",
  "Asthma/Skin": "bg-[#fdeceb] text-[#e0567a]",
  Asthma: "bg-[#fdeceb] text-[#e0567a]",
  Allergy: "bg-[#f0ecfb] text-[#6d4bd8]",
  Gut: "bg-[#fdf2e8] text-[#e08a2b]",
  "Headache/Flushing": "bg-[#eef1fb] text-[#2b7fd9]",
  Headache: "bg-[#eef1fb] text-[#2b7fd9]",
  Health: "bg-slate-100 text-slate-600",
  Low: "bg-[#e9f7ef] text-[#22a35a]",
};

export default function FoodGuide() {
  const navigate = useNavigate();
  const { user } = useAsaAuth();
  const fullAccess = hasFullAccess(user);
  const [tab, setTab] = useState("foods");
  const [q, setQ] = useState("");

  const eFiltered = E_NUMBERS.filter(
    (e) => e.code.toLowerCase().includes(q.toLowerCase()) || e.name.toLowerCase().includes(q.toLowerCase())
  );
  const proFoods = fullAccess ? PRO_INFLAMMATORY : PRO_INFLAMMATORY.slice(0, 20);
  const antiFoods = fullAccess ? ANTI_INFLAMMATORY : ANTI_INFLAMMATORY.slice(0, 20);

  return (
    <AppLayout title="Food & Additives Guide">
      <div className="bg-gradient-to-r from-[#0d9488] to-[#0f766e] rounded-2xl p-6 text-white flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center"><Leaf size={26} /></div>
        <div>
          <h2 className="font-head font-bold text-xl">Food & inflammatory dietary patterns</h2>
          <p className="text-white/85 text-sm mt-0.5">Educational information on foods and additives commonly discussed in relation to inflammation. Foods affect everyone differently.</p>
        </div>
      </div>

      {/* Tabs (E-numbers is ASAL Pro) */}
      <div className="flex gap-2 mt-5">
        {[{ k: "foods", l: "Foods" }, ...(fullAccess ? [{ k: "enumbers", l: "E-Numbers" }] : [])].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === t.k ? "bg-[#0d9488] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#0d9488]"}`}>{t.l}</button>
        ))}
      </div>

      {tab === "foods" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h3 className="font-head font-semibold text-slate-800 flex items-center gap-2 mb-1"><Flame size={18} className="text-[#e0567a]" /> Foods commonly associated with inflammatory dietary patterns</h3>
            <p className="text-[12.5px] text-slate-400 mb-3">These foods are often discussed in relation to inflammation. They do not have identical effects in every person.</p>
            <div className="space-y-3">
              {proFoods.map((f) => (
                <div key={f.name} className="border-l-2 border-[#e0567a] pl-3">
                  <p className="font-semibold text-slate-800 text-[14.5px]">{f.name}</p>
                  <p className="text-[13px] text-slate-500 leading-relaxed">{f.effect}</p>
                </div>
              ))}
              {!fullAccess && (
                <button onClick={() => navigate("/asa/pricing")} data-testid="foodguide-seemore" className="w-full flex items-center gap-2 border-l-2 border-[#0d9488] pl-3 py-2 mt-1 text-left hover:bg-slate-50 rounded-r-lg transition-colors">
                  <Lock size={15} className="text-[#0f766e] shrink-0" />
                  <span className="text-[13px] font-semibold text-[#0f766e]">Showing {proFoods.length} of {PRO_INFLAMMATORY.length} foods — subscribe to ASAL Pro to see the full list</span>
                </button>
              )}
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h3 className="font-head font-semibold text-slate-800 flex items-center gap-2 mb-1"><Leaf size={18} className="text-[#22a35a]" /> Foods often part of an anti-inflammatory pattern</h3>
            <p className="text-[12.5px] text-slate-400 mb-3">These foods are often part of a broadly anti-inflammatory way of eating. No single food prevents or treats disease.</p>
            <div className="space-y-3">
              {antiFoods.map((f) => (
                <div key={f.name} className="border-l-2 border-[#22a35a] pl-3">
                  <p className="font-semibold text-slate-800 text-[14.5px]">{f.name}</p>
                  <p className="text-[13px] text-slate-500 leading-relaxed">{f.effect}</p>
                </div>
              ))}
              {!fullAccess && (
                <button onClick={() => navigate("/asa/pricing")} data-testid="foodguide-seemore-anti" className="w-full flex items-center gap-2 border-l-2 border-[#0d9488] pl-3 py-2 mt-1 text-left hover:bg-slate-50 rounded-r-lg transition-colors">
                  <Lock size={15} className="text-[#0f766e] shrink-0" />
                  <span className="text-[13px] font-semibold text-[#0f766e]">Showing {antiFoods.length} of {ANTI_INFLAMMATORY.length} foods — subscribe to ASAL Pro to see the full list</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {!fullAccess && (
        <div className="rounded-2xl mt-5 p-5 bg-gradient-to-r from-[#0d9488] to-[#0f766e] text-white flex flex-col sm:flex-row sm:items-center gap-3" data-testid="foodguide-upgrade-card">
          <Lock size={22} className="shrink-0" />
          <div className="flex-1">
            <p className="font-head font-semibold">Unlock the complete food & lifestyle guide</p>
            <p className="text-white/90 text-[13px] mt-0.5">You're seeing a preview of 8 foods in each list. ASAL Pro unlocks the complete inflammatory & anti-inflammatory food lists, the full E-numbers/additives database and lifestyle guidance.</p>
          </div>
          <button onClick={() => navigate("/asa/pricing")} className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-white text-[#0f766e] font-semibold text-sm shrink-0"><Sparkles size={16} /> See ASAL Pro</button>
        </div>
      )}

      {tab === "enumbers" && (
        <div className="mt-5">
          <div className="relative mb-4 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search E-number or name…" className="w-full h-11 pl-10 pr-4 rounded-lg border border-slate-200 text-sm outline-none focus:border-[#0d9488] bg-white" />
          </div>
          <div className="overflow-x-auto kl-scroll border border-slate-200 rounded-xl bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-[12px] text-slate-500 uppercase tracking-wide">
                  <th className="px-4 py-3 font-semibold">Code</th>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Type</th>
                  <th className="px-4 py-3 font-semibold">Concern</th>
                  <th className="px-4 py-3 font-semibold">Reported effect on skin/health</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {eFiltered.map((e) => (
                  <tr key={e.code} className="hover:bg-slate-50 transition-colors align-top">
                    <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">{e.code}</td>
                    <td className="px-4 py-3 text-slate-700">{e.name}</td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{e.type}</td>
                    <td className="px-4 py-3"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${concernStyle[e.concern] || "bg-slate-100 text-slate-600"}`}>{e.concern}</span></td>
                    <td className="px-4 py-3 text-slate-500 leading-relaxed">{e.effect}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-4 mt-6">
        <AlertTriangle size={18} className="text-amber-500 shrink-0 mt-0.5" />
        <p className="text-[13px] text-amber-700">{FOOD_DISCLAIMER}</p>
      </div>
    </AppLayout>
  );
}
